import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { Command, CommandGroup, CommandSection } from '../lib/command-palette';
import { CommandPaletteOption } from './CommandPaletteOption';

// One group inside the palette's listbox (heading + options). Presenter — the
// dialog owns the sections, the cursor and the navigation.

const HEADING: Record<CommandGroup, string> = {
  pages: 'palette.group_pages',
  repos: 'palette.group_repos',
  sessions: 'palette.group_sessions',
};

/** A group's visible name — also what `CommandPaletteDialog` announces a refused source by. */
export function groupHeading(key: CommandGroup): string {
  return t(HEADING[key]);
}

export function CommandPaletteGroup(props: {
  readonly section: CommandSection;
  readonly activeId: string | undefined;
  readonly onPick: (command: Command) => void;
}): JSX.Element {
  const heading = (): string => groupHeading(props.section.key);
  // A group with nothing to say renders nothing — a failed source still speaks.
  return (
    <Show when={props.section.rows.length > 0 || props.section.failed}>
      {/* biome-ignore lint/a11y/useSemanticElements: `group` is the ARIA role a
          listbox may own between itself and its options; biome's <fieldset>
          suggestion is a form control and would break `aria-required-parent`. */}
      <div class="palette__group" role="group" aria-label={heading()}>
        <p class="palette__group-head" aria-hidden="true">
          {heading()}
        </p>
        <For each={props.section.rows}>
          {(command) => (
            <CommandPaletteOption
              command={command}
              active={command.id === props.activeId}
              onPick={props.onPick}
            />
          )}
        </For>
        {/* The source refused. Said out loud, because "no repos matched" and
            "the repo list never loaded" send a reader to opposite conclusions.
            No `role="status"` HERE: a listbox's group may own only options
            (axe `aria-required-children`, 19-S5), so the live announcement
            is `CommandPaletteDialog`'s, from outside the listbox. */}
        <Show when={props.section.failed}>
          <p class="palette__note">{t('palette.group_failed')}</p>
        </Show>
        {/* Truncation is never silent (the artifacts/epics rule). */}
        <Show when={props.section.hidden > 0}>
          <p class="palette__note">
            {t('palette.group_more', { count: String(props.section.hidden) })}
          </p>
        </Show>
      </div>
    </Show>
  );
}
